<template>
    <div class="inbox-reply-box" @paste="onPaste">
        <div class="inbox-reply-header">
            <v-icon small class="mr-1">{{
                mode === "forward"
                    ? "mdi-share"
                    : mode === "reply-all"
                    ? "mdi-reply-all"
                    : "mdi-reply"
            }}</v-icon>
            <span v-if="mode === 'forward'">Forward as email</span>
            <span v-else-if="mode === 'reply-all'">Reply all</span>
            <span v-else>Reply to {{ replyTo }}</span>
        </div>

        <v-text-field
            v-if="mode === 'forward' || mode === 'reply-all'"
            v-model="to"
            label="To"
            placeholder="recipient@example.com"
            hide-details
            class="inbox-reply-field"
        ></v-text-field>
        <v-text-field
            v-model="cc"
            label="Cc"
            placeholder="optional, comma-separated"
            hide-details
            class="inbox-reply-field"
        ></v-text-field>
        <v-text-field
            v-model="bcc"
            label="Bcc"
            placeholder="optional, comma-separated"
            hide-details
            class="inbox-reply-field"
        ></v-text-field>
        <v-text-field
            v-if="mode === 'forward'"
            v-model="subject"
            label="Subject"
            hide-details
            class="inbox-reply-field"
        ></v-text-field>
        <v-textarea
            v-model="body"
            :placeholder="bodyPlaceholder"
            auto-grow
            hide-details
            :rows="mode === 'forward' ? 5 : 3"
            class="inbox-reply-textarea"
        ></v-textarea>

        <div v-if="attachments.length" class="inbox-compose-files">
            <div
                v-for="(file, index) in attachments"
                :key="file.key"
                class="inbox-compose-file"
            >
                <img
                    v-if="file.previewUrl"
                    :src="file.previewUrl"
                    class="inbox-compose-file-thumb"
                    alt=""
                />
                <v-icon v-else small color="#78909c">mdi-paperclip</v-icon>
                <span class="inbox-compose-file-name">{{ file.fileName }}</span>
                <v-btn icon small class="ml-1" @click="removeAttachment(index)">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </div>
        </div>

        <v-layout row wrap align-center class="mt-2 inbox-compose-actions">
            <input
                ref="fileInput"
                type="file"
                multiple
                class="inbox-compose-file-input"
                @change="onFileChange"
            />
            <v-btn
                small
                outline
                color="success"
                class="mr-2"
                @click="pickFiles"
            >
                <v-icon small class="mr-1">mdi-paperclip</v-icon>
                Attach file
            </v-btn>
            <v-btn
                small
                outline
                color="success"
                @click="pasteImage"
            >
                <v-icon small class="mr-1">mdi-image</v-icon>
                Paste image
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="default" dark small @click="$emit('cancel')">
                Cancel
            </v-btn>
            <v-btn
                color="success"
                small
                class="ml-2 mr-0"
                :loading="sending"
                :disabled="!canSend"
                @click="submit"
            >
                <v-icon small class="mr-1">mdi-send</v-icon>
                {{
                    mode === "forward"
                        ? "Send"
                        : mode === "reply-all"
                        ? "Send reply all"
                        : "Send reply"
                }}
            </v-btn>
        </v-layout>
    </div>
</template>

<script>
var MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
var MAX_ATTACHMENTS = 8;

export default {
    name: "InboxEmailCompose",
    props: {
        mode: {
            type: String,
            default: "reply",
        },
        replyTo: {
            type: String,
            default: "",
        },
        bodyPlaceholder: {
            type: String,
            default: "Type your reply...",
        },
        initialTo: {
            type: String,
            default: "",
        },
        initialCc: {
            type: String,
            default: "",
        },
        initialBcc: {
            type: String,
            default: "",
        },
        initialSubject: {
            type: String,
            default: "",
        },
        initialBody: {
            type: String,
            default: "",
        },
        initialAttachments: {
            type: Array,
            default: function() {
                return [];
            },
        },
        requireBody: {
            type: Boolean,
            default: true,
        },
        sending: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            to: this.initialTo || "",
            cc: this.initialCc || "",
            bcc: this.initialBcc || "",
            subject: this.initialSubject || "",
            body: this.initialBody || "",
            attachments: [],
            pasteIndex: 1,
        };
    },
    watch: {
        initialAttachments: {
            immediate: true,
            handler: function(files) {
                this.seedExistingAttachments(files);
            },
        },
    },
    computed: {
        canSend: function() {
            if (
                (this.mode === "forward" || this.mode === "reply-all") &&
                !(this.to && this.to.trim())
            ) {
                return false;
            }
            if (this.requireBody && !(this.body && this.body.trim())) {
                return false;
            }
            return true;
        },
    },
    methods: {
        pickFiles: function() {
            var input = this.$refs.fileInput;
            if (input && input.click) {
                input.click();
            }
        },
        onFileChange: function(event) {
            var files = event && event.target && event.target.files;
            this.addFiles(files);
            if (event && event.target) {
                event.target.value = "";
            }
        },
        onPaste: function(event) {
            var clipboard = event && event.clipboardData;
            if (!clipboard || !clipboard.items) {
                return;
            }

            var files = [];
            var i;
            for (i = 0; i < clipboard.items.length; i++) {
                var item = clipboard.items[i];
                if (item.kind === "file" && item.type && item.type.indexOf("image/") === 0) {
                    var file = item.getAsFile();
                    if (file) {
                        files.push(file);
                    }
                }
            }

            if (files.length) {
                event.preventDefault();
                this.addFiles(files);
            }
        },
        pasteImage: function() {
            var self = this;
            if (navigator.clipboard && navigator.clipboard.read) {
                navigator.clipboard
                    .read()
                    .then(function(items) {
                        var files = [];
                        var chain = Promise.resolve();
                        items.forEach(function(item) {
                            var types = item.types || [];
                            var imageType = null;
                            var i;
                            for (i = 0; i < types.length; i++) {
                                if (types[i].indexOf("image/") === 0) {
                                    imageType = types[i];
                                    break;
                                }
                            }
                            if (!imageType) {
                                return;
                            }
                            chain = chain.then(function() {
                                return item.getType(imageType).then(function(blob) {
                                    var name =
                                        "pasted-image-" +
                                        self.pasteIndex +
                                        "." +
                                        (imageType.split("/")[1] || "png");
                                    self.pasteIndex += 1;
                                    files.push(
                                        new File([blob], name, { type: imageType }),
                                    );
                                });
                            });
                        });
                        return chain.then(function() {
                            if (!files.length) {
                                self.notifyNoImage();
                                return;
                            }
                            self.addFiles(files);
                        });
                    })
                    .catch(function() {
                        self.notifyNoImage();
                    });
                return;
            }

            this.notifyNoImage();
        },
        notifyNoImage: function() {
            this.$notify({
                group: "notification",
                text: "No image on the clipboard. Copy an image and try again, or paste into the message box.",
                type: "error",
            });
        },
        seedExistingAttachments: function(files) {
            var list = files || [];
            var seen = {};
            var i;
            for (i = 0; i < this.attachments.length; i++) {
                if (this.attachments[i].attachmentId) {
                    seen[this.attachments[i].attachmentId] = true;
                }
            }

            var seeded = [];
            for (i = 0; i < list.length; i++) {
                var file = list[i];
                var attachmentId = file && (file.attachmentId || file.id || file.ID);
                if (!file || !attachmentId || seen[attachmentId]) {
                    continue;
                }
                seen[attachmentId] = true;
                var fileName = file.fileName || file.FileName || "attachment";
                var contentType =
                    file.contentType || file.ContentType || "application/octet-stream";
                var previewUrl = file.previewUrl || file.fileURL || file.FileURL || "";
                seeded.push({
                    key: "existing-" + attachmentId,
                    fileName: fileName,
                    contentType: contentType,
                    base64: "",
                    previewUrl: this.isImageAttachment(contentType, fileName) ? previewUrl : "",
                    attachmentId: attachmentId,
                });
            }

            if (seeded.length) {
                this.attachments = seeded.concat(this.attachments);
            }
        },
        isImageAttachment: function(contentType, fileName) {
            var type = (contentType || "").toLowerCase();
            if (type.indexOf("image/") === 0) {
                return true;
            }
            return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName || "");
        },
        addFiles: function(fileList) {
            if (!fileList || !fileList.length) {
                return;
            }

            var i;
            for (i = 0; i < fileList.length; i++) {
                this.addFile(fileList[i]);
            }
        },
        addFile: function(file) {
            if (!file) {
                return;
            }
            if (this.attachments.length >= MAX_ATTACHMENTS) {
                this.$notify({
                    group: "notification",
                    text: "You can attach up to " + MAX_ATTACHMENTS + " files",
                    type: "error",
                });
                return;
            }
            if (file.size > MAX_ATTACHMENT_BYTES) {
                this.$notify({
                    group: "notification",
                    text: (file.name || "File") + " is larger than 8 MB",
                    type: "error",
                });
                return;
            }

            var self = this;
            var reader = new FileReader();
            reader.onload = function() {
                var result = reader.result || "";
                var parts = String(result).split(",");
                var base64 = parts.length > 1 ? parts[1] : "";
                var isImage = !!(file.type && file.type.indexOf("image/") === 0);
                self.attachments.push({
                    key: Date.now() + "-" + self.attachments.length + "-" + (file.name || "file"),
                    fileName: file.name || "attachment",
                    contentType: file.type || "application/octet-stream",
                    base64: base64,
                    previewUrl: isImage ? result : "",
                });
            };
            reader.readAsDataURL(file);
        },
        removeAttachment: function(index) {
            this.attachments.splice(index, 1);
        },
        submit: function() {
            if (!this.canSend) {
                return;
            }

            this.$emit("send", {
                to: this.to,
                cc: this.cc,
                bcc: this.bcc,
                subject: this.subject,
                body: this.body,
                attachments: this.attachments.map(function(file) {
                    return {
                        fileName: file.fileName,
                        contentType: file.contentType,
                        base64: file.base64,
                        attachmentId: file.attachmentId || null,
                    };
                }),
            });
        },
    },
};
</script>

<style>
.inbox-compose-file-input {
    display: none;
}

.inbox-compose-files {
    margin-top: 8px;
}

.inbox-compose-file {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #dce3e8;
    border-radius: 4px;
    padding: 2px 4px 2px 8px;
    margin-bottom: 6px;
    font-size: 12px;
    color: #455a64;
}

.inbox-compose-file-thumb {
    width: 28px;
    height: 28px;
    object-fit: cover;
    border-radius: 3px;
    margin-right: 8px;
}

.inbox-compose-file-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.inbox-compose-actions {
    margin-left: 0 !important;
    margin-right: 0 !important;
}
</style>
